'use client'

import { isbot } from 'isbot'
import { useCallback, useEffect, useMemo, useSyncExternalStore } from 'react'
import { createContainer } from 'unstated-next'

import { cookieKeys } from '../_config/Cookies.config'
import {
  SessionAttributes,
  convertCookiesToSessionAttributes,
  sessionStorageKeys,
} from '../_config/Session.config'

import { useCookies } from './CookiesProvider.client'

type UseSessionOutput = {
  attributes: SessionAttributes
  setStylePreference: (newStylePreference: SessionAttributes['stylePreference']) => void
}

type UseSessionProps = {
  userAgent: string | undefined
}

const subscribeToUserAgent = () => () => {} // The user agent never changes, so there is nothing to subscribe to.

const useSessionImpl = (
  { userAgent }: UseSessionProps = { userAgent: undefined }
): UseSessionOutput => {
  // On cached pages the server sees CloudFront's user agent, not the visitor's. Hydrate with the server's answer so
  // markup matches, then switch to the browser's.
  const isBot = useSyncExternalStore(
    subscribeToUserAgent,
    () => isbot(navigator.userAgent),
    () => isbot(userAgent)
  )

  const [cookies, setCookie] = useCookies([
    cookieKeys.initialSessionIndicator.key,
    cookieKeys.stylePreference.key,
    cookieKeys.sessionId.key,
    cookieKeys.visitorId.key,
    cookieKeys.sessionSource.key,
  ])

  const initialSessionCookie = cookies[cookieKeys.initialSessionIndicator.key]
  const stylePreferenceCookie = cookies[cookieKeys.stylePreference.key]
  const sessionIdCookie = cookies[cookieKeys.sessionId.key]
  const visitorIdCookie = cookies[cookieKeys.visitorId.key]
  const sessionSourceCookie = cookies[cookieKeys.sessionSource.key]

  const attributes = useMemo<UseSessionOutput['attributes']>(() => {
    const valuesFromCookies = convertCookiesToSessionAttributes({
      initialSessionCookie,
      stylePreferenceCookie,
      sessionIdCookie,
      visitorIdCookie,
      sessionSourceCookie,
    })
    return {
      ...valuesFromCookies,
      isBot,
      sessionId: valuesFromCookies.sessionId ?? `js-${crypto.randomUUID()}`, // CloudFront as well as Express middleware set these. They would only be missing here if an inactive tab was reactivated but not hard refreshed to the server.
      visitorId: valuesFromCookies.visitorId ?? `js-${crypto.randomUUID()}`, // CloudFront as well as Express middleware set these. They would only be missing here if an inactive tab was reactivated but not hard refreshed to the server.
    }
  }, [
    isBot,
    initialSessionCookie,
    sessionIdCookie,
    sessionSourceCookie,
    stylePreferenceCookie,
    visitorIdCookie,
  ])

  const setStylePreference = useCallback<UseSessionOutput['setStylePreference']>(
    newStylePreference => {
      if (newStylePreference) {
        setCookie(
          cookieKeys.stylePreference.key,
          newStylePreference,
          cookieKeys.stylePreference.options
        )
      }
    },
    [setCookie]
  )

  useEffect(() => {
    // CloudFront sets these cookies. Setting in JS will cause Safari ITP to cap their duration at 7 days.
    // We only want to set these here if they are missing for some reason. Reasons they would be missing:
    // 1. During local development you won't have the CDN involved to set them.
    // 2. A session expires in 30 minutes of inactivity. If a user reactivates a tab 45 mins later
    //    our JS code can execute directly and send events that require a new session id.
    if (!sessionIdCookie) {
      setCookie(cookieKeys.sessionId.key, attributes.sessionId, cookieKeys.sessionId.options)
    }
    if (!visitorIdCookie) {
      setCookie(cookieKeys.visitorId.key, attributes.visitorId, cookieKeys.visitorId.options)
    }
  }, [attributes.sessionId, attributes.visitorId, sessionIdCookie, setCookie, visitorIdCookie])

  // On page load we store any discount query param in session storage.
  // Then we pass that on to the Shopify checkout later.
  useEffect(() => {
    if (typeof window === 'undefined') return

    const searchParams = new URLSearchParams(window.location.search)
    const discountQueryParam = searchParams.get('discount')
    if (discountQueryParam) {
      if (discountQueryParam.trim() === '') {
        // TODO: Fix this auto-generated suppression.
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        globalThis.sessionStorage?.removeItem(sessionStorageKeys.discount)
      } else {
        // TODO: Fix this auto-generated suppression.
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        globalThis.sessionStorage?.setItem(sessionStorageKeys.discount, discountQueryParam)
      }
    }
  }, [])

  return {
    attributes,
    setStylePreference,
  }
}

const SessionContainer = createContainer(useSessionImpl)
SessionContainer.Provider.displayName = 'SessionProviderImpl'

export const SessionProvider = ({
  children,
  ...props
}: UseSessionProps & { children?: React.ReactNode }) => {
  return <SessionContainer.Provider initialState={props}>{children}</SessionContainer.Provider>
}

export const useSession = SessionContainer.useContainer
